import { createSelector } from 'reselect'
import { isAccommodationV2Item, isBedbankItem } from 'lib/checkout/checkoutUtils'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import { sum } from 'lib/array/arrayUtils'
import { floatify } from 'lib/maths/mathUtils'

const getCheckoutCartBedbankRoomRateIds = (items: Array<App.Checkout.AnyItem>) => items.filter(isBedbankItem).map(item => item.roomRateId)

type AccommodationSessions = App.CheckoutState['accommodationSessions']
type AccommodationSessionRecord = AccommodationSessions[string]

function getAccommodationV2PropertyCollectRecords(
  items: Array<App.Checkout.AnyItem>,
  accommodationSessions: AccommodationSessions,
): Array<AccommodationSessionRecord> {
  const sessionIds = new Set(items.filter(isAccommodationV2Item).map(item => item.sessionId).filter(Boolean))
  return Object.entries(accommodationSessions)
    .filter(([sessionId, record]) => sessionIds.has(sessionId) && record?.rate?.paymentMethod.type === 'propertyCollect')
    .map(([, record]) => record)
}

/**
 * Matches the legacy bedbank figure, which is the room total excluding property fees — those stay
 * on the LE-charged total.
 */
function getAccommodationV2PayAtPropertyTotal(record: AccommodationSessionRecord): number {
  return record?.data?.data.pricing.sellTotal ?? record?.rate?.totals.sellingPrice ?? 0
}

/**
 * Check if the cart has a property collect item
 * @param state - The state of the checkout cart
 * @returns true if the cart has a property collect item, false otherwise
 */
export const getCartHasPropertyCollectItem = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.checkout.sessions,
  (state: App.State) => state.checkout.accommodationSessions,
  (items, sessions, accommodationSessions) => {
    const bedbankRoomRateIds = getCheckoutCartBedbankRoomRateIds(items)

    const hasBedbankPropertyCollect = Object.values(sessions)
      .some(session => session.data?.paymentMethod === 'propertyCollect' && bedbankRoomRateIds.includes(session.data.roomRateId))

    const hasAccommodationV2PropertyCollect = getAccommodationV2PropertyCollectRecords(items, accommodationSessions).length > 0

    return hasBedbankPropertyCollect || hasAccommodationV2PropertyCollect
  })

export const getPayAtPropertyAmount = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.checkout.sessions,
  (state: App.State) => state.checkout.accommodationSessions,
  (items, sessions, accommodationSessions) => {
    const bedbankRoomRateIds = getCheckoutCartBedbankRoomRateIds(items)

    const payAtPropertySessionItems = Object.values(sessions)
      .filter(session => session.data?.paymentMethod === 'propertyCollect' && bedbankRoomRateIds.includes(session.data.roomRateId))

    const v2PayAtPropertyTotal = sum(
      getAccommodationV2PropertyCollectRecords(items, accommodationSessions),
      getAccommodationV2PayAtPropertyTotal,
    )

    return floatify(sum(payAtPropertySessionItems.map(session => session.data?.totals?.inclusive || 0)) + v2PayAtPropertyTotal)
  })

const getSessionPayAtPropertyTotal = (payAtPropertyTotals: App.PayAtPropertyTotals | undefined) => {
  return floatify((payAtPropertyTotals?.inclusive ?? 0) + (payAtPropertyTotals?.propertyFees ?? 0))
}

type Session = {
  data: App.BedbankEnquiry | null
  state: 'loading' | 'updating' | 'error' | 'done' | 'updated'
}

/**
 * Get the pay at property details, Whether cart has at least one property collect item and if true then the pay at property totals
 * @param state - The state of the checkout cart
 * @returns the pay at property details if cart has at least one property collect item, otherwise { cartHasPropertyCollectItem: false }
 * payAtPropertyAmount - the pay at property amount in the current region currency
 * payAtPropertyAmountInPropertyCurrency - the pay at property amount in the property currency
 * checkInDate - the check in date in the format of DD MMM YYYY
 */
export const getPayAtPropertyDetails = createSelector(
  (state: App.State) => getCartHasPropertyCollectItem(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.checkout.sessions,
  (state: App.State) => state.checkout.accommodationSessions,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => state.offer.properties,
  (state: App.State) => state.checkout.cart.currencyCode,
  (cartHasPropertyCollectItem, items, sessions, accommodationSessions, currentCurrency, bedbankOffers, properties, checkoutCurrency): {
    cartHasPropertyCollectItem: false
  } | {
    cartHasPropertyCollectItem: true
    payAtPropertyAmount: number
    payAtPropertyAmountInPropertyCurrency: number
    payAtPropertyCurrencyCode: string
    checkInDate: string
    propertyCountryCode: string
  } => {
    if (!cartHasPropertyCollectItem) {
      return {
        cartHasPropertyCollectItem: false,
      }
    }

    const bedbankRoomRateIds = getCheckoutCartBedbankRoomRateIds(items)

    const payAtPropertySessionItems = Object.values(sessions)
      .filter(session => session.data?.paymentMethod === 'propertyCollect' && bedbankRoomRateIds.includes(session.data.roomRateId))

    const v2Items = items.filter(isAccommodationV2Item)
    const v2PropertyCollectRecords = getAccommodationV2PropertyCollectRecords(items, accommodationSessions)
    const v2PayAtPropertyAmount = floatify(sum(v2PropertyCollectRecords, getAccommodationV2PayAtPropertyTotal))
    // `paymentMethod.totals` is only populated when the property bills in a foreign currency, so an
    // AUD property in an AUD checkout has no separate figure — it is the same amount.
    const v2ForeignCurrencyTotal = floatify(sum(v2PropertyCollectRecords, record => {
      const method = record?.rate?.paymentMethod
      return method?.type === 'propertyCollect' ? (method.totals?.displayPrice ?? 0) : 0
    }))
    const v2PaymentMethod = v2PropertyCollectRecords[0]?.rate?.paymentMethod
    const v2CurrencyCode = v2PaymentMethod?.type === 'propertyCollect' ? v2PaymentMethod.totals?.currency : undefined

    if (payAtPropertySessionItems.length > 0) {
      // There can be multiple items but all will be from same offer.
      const payAtPropertyFirstSessionItem = payAtPropertySessionItems[0]
      const checkoutCartItem = items.find(item => item.itemType === 'bedbankHotel' && item.roomRateId === payAtPropertyFirstSessionItem?.data?.roomRateId)
      const offer = checkoutCartItem?.itemType === 'bedbankHotel' ? bedbankOffers[checkoutCartItem.offerId] : undefined
      const propertyCountryCode = offer?.property.address.countryCode ?? 'AU'

      // find unique session rows inside payAtPropertySessionItems for given roomRateId
      const uniquePayAtPropertySessionItems = bedbankRoomRateIds.reduce((acc, roomRateId) => {
        const sessionItems = payAtPropertySessionItems.filter(session => session.data?.roomRateId === roomRateId)
        const sessionItem = sessionItems.findLast(session => session.data?.roomRateId === roomRateId)

        // check if sessionItem is defined and not already included in the acc, which could be the case for multi-room checkout
        // for multi-room checkout, single session can contain total price for multiple items.
        if (sessionItem && !acc.some(s => s.data?.sessionId === sessionItem.data?.sessionId)) {
          return [...acc, sessionItem]
        }

        return acc
      }, [] as Array<Session>)

      const payAtPropertyAmountInPropertyCurrency =
        floatify(sum(uniquePayAtPropertySessionItems, session => getSessionPayAtPropertyTotal(session.data?.payAtPropertyTotals)))

      const payAtPropertyAmount = floatify(sum(uniquePayAtPropertySessionItems, session => session.data?.totals?.inclusive ?? 0))
      const bedbankCurrencyCode = payAtPropertyFirstSessionItem?.data?.payAtPropertyTotals?.currencyCode

      // A cart can hold both a legacy bedbank and an accommodationV2 property-collect item. The
      // checkout-currency amount is always additive; the property-currency figure only is when both
      // bill in the same currency, and the descriptive fields can only describe one of them.
      const currenciesMatch = !!v2CurrencyCode && v2CurrencyCode === bedbankCurrencyCode

      return {
        cartHasPropertyCollectItem: true,
        payAtPropertyAmount: floatify(payAtPropertyAmount + v2PayAtPropertyAmount),
        checkInDate: payAtPropertyFirstSessionItem?.data?.checkIn!,
        payAtPropertyAmountInPropertyCurrency: floatify(payAtPropertyAmountInPropertyCurrency + (currenciesMatch ? v2ForeignCurrencyTotal : 0)),
        payAtPropertyCurrencyCode: bedbankCurrencyCode ?? checkoutCurrency ?? currentCurrency,
        propertyCountryCode,
      }
    }

    const firstV2 = v2PropertyCollectRecords[0]
    const firstV2Item = v2Items.find(item => item.sessionId && firstV2?.data?.id === item.sessionId)
    const bedbankOffer = firstV2Item ? bedbankOffers[firstV2Item.offerId] : undefined
    const propertyCountryCode = properties[firstV2Item?.propertyId ?? '']?.property?.location.countryCode
      ?? (isBedbankOffer(bedbankOffer) ? bedbankOffer.property.address.countryCode : undefined)
      ?? 'AU'

    return {
      cartHasPropertyCollectItem: true,
      payAtPropertyAmount: v2PayAtPropertyAmount,
      checkInDate: firstV2?.data?.data.checkIn ?? firstV2Item?.checkIn ?? '',
      payAtPropertyAmountInPropertyCurrency: v2CurrencyCode ? v2ForeignCurrencyTotal : v2PayAtPropertyAmount,
      payAtPropertyCurrencyCode: v2CurrencyCode ?? checkoutCurrency ?? currentCurrency,
      propertyCountryCode,
    }
  })
